/**
 * Collision-free comune labels for the Zone map (`ZoneMap.tsx`).
 *
 * 82 comuni do not all fit a name at a readable size (the Valsesia packs a
 * dozen along one valley), so every comune gets its dot and a name only where
 * one fits: greedily, in priority order, each label tries eight spots around
 * its dot (right, left, above, below, then the diagonals) and takes the first
 * that stays inside the frame and clear of every placed label, every other
 * dot and the fixed obstacles (landmark and river names). Unnamed dots keep
 * their name in a `<title>`, and the Zone page's table lists every comune.
 *
 * Text is not measured (no DOM in the prerender): widths come from a small
 * per-glyph table for the labels' sans semibold, slightly generous so a real
 * glyph run never overshoots its box. Pure + − × ÷ and comparisons, rounded
 * with `round2` — the prerendered SVG and the first client render agree.
 */

import { round2 } from './mapProjection'

export interface LabelPoint {
  key: string
  text: string
  x: number
  y: number
}

export interface Box {
  x0: number
  y0: number
  x1: number
  y1: number
}

export interface PlacedLabel {
  key: string
  x: number
  y: number
  anchor: 'start' | 'middle' | 'end'
}

export interface LayoutOptions {
  width: number
  height: number
  /** Label font size, user units. */
  fontSize: number
  /** Dot radius: every label keeps off every dot by this much. */
  dotR: number
  /** Dot edge → text, user units. */
  gap: number
  /** Breathing room around each label box (the halo), user units. */
  pad: number
  /** Fixed areas no label may cover (landmark names, river names…). */
  obstacles?: readonly Box[]
}

/** Advance widths in em for the labels' sans semibold (Inter-like), rounded up. */
const NARROW = new Set([...'iljìíI.,\'’|!:;'])
const SEMI = new Set([...'frt()-'])
const WIDE = new Set([...'mwMW'])
export function textWidth(text: string, fontSize: number): number {
  let em = 0
  for (const ch of text) {
    if (ch === ' ') em += 0.27
    else if (NARROW.has(ch)) em += 0.29
    else if (SEMI.has(ch)) em += 0.4
    else if (WIDE.has(ch)) em += 0.88
    else if (ch >= 'A' && ch <= 'Z') em += 0.7
    else em += 0.59
  }
  return em * fontSize
}

/** A text's box for a baseline point and anchor (ascender 0.78 em, descender 0.24 em). */
export function textBox(x: number, y: number, w: number, fontSize: number, anchor: PlacedLabel['anchor']): Box {
  const x0 = anchor === 'start' ? x : anchor === 'end' ? x - w : x - w / 2
  return { x0, y0: y - 0.78 * fontSize, x1: x0 + w, y1: y + 0.24 * fontSize }
}

export function overlaps(a: Box, b: Box): boolean {
  return a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1
}

const grow = (b: Box, d: number): Box => ({ x0: b.x0 - d, y0: b.y0 - d, x1: b.x1 + d, y1: b.y1 + d })

/**
 * Places labels for `points` in the given (priority) order; `dots` are every
 * dot on the map (labels avoid them all but their own). Returns only the
 * labels that fit, in placement order.
 */
export function layoutLabels(points: readonly LabelPoint[], dots: readonly { key: string; x: number; y: number }[], o: LayoutOptions): PlacedLabel[] {
  const f = o.fontSize
  const off = o.dotR + o.gap
  const placed: Box[] = []
  const out: PlacedLabel[] = []
  const obstacles = o.obstacles ?? []
  const dotBoxes = dots.map((d) => ({ key: d.key, box: grow({ x0: d.x, y0: d.y, x1: d.x, y1: d.y }, o.dotR + 1) }))
  const inFrame = (b: Box) => b.x0 >= 2 && b.y0 >= 2 && b.x1 <= o.width - 2 && b.y1 <= o.height - 2

  for (const p of points) {
    const w = textWidth(p.text, f)
    // Baseline points: right/left sit the text's x-height on the dot's centre line.
    const mid = p.y + 0.34 * f
    const candidates: [number, number, PlacedLabel['anchor']][] = [
      [p.x + off, mid, 'start'],
      [p.x - off, mid, 'end'],
      [p.x, p.y - off - 0.24 * f, 'middle'],
      [p.x, p.y + off + 0.78 * f, 'middle'],
      [p.x + off * 0.7, p.y - off * 0.7 - 0.1 * f, 'start'],
      [p.x - off * 0.7, p.y - off * 0.7 - 0.1 * f, 'end'],
      [p.x + off * 0.7, p.y + off * 0.7 + 0.7 * f, 'start'],
      [p.x - off * 0.7, p.y + off * 0.7 + 0.7 * f, 'end'],
    ]
    for (const [x, y, anchor] of candidates) {
      const box = textBox(x, y, w, f, anchor)
      const halo = grow(box, o.pad)
      if (!inFrame(box)) continue
      if (placed.some((b) => overlaps(halo, b))) continue
      if (obstacles.some((b) => overlaps(halo, b))) continue
      if (dotBoxes.some((d) => d.key !== p.key && overlaps(halo, d.box))) continue
      placed.push(halo)
      out.push({ key: p.key, x: round2(x), y: round2(y), anchor })
      break
    }
  }
  return out
}
